import { Fragment, useState } from 'react';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

import type { FormResponse, FormField } from '@/types/form';

import styles from './ResponseTable.module.css';

interface ResponseTableProps {
    responses: FormResponse[];
    fields: FormField[];
    onRowClick?: (response: FormResponse) => void;
}

function renderValue(value: string): string {
    if (!value) return '—';
    // Multiple choice values are stored as "A||B||C"
    if (value.includes('||')) {
        return value.split('||').join(', ');
    }
    return value;
}

export function ResponseTable({ responses, fields, onRowClick }: ResponseTableProps) {
    const [expandedRow, setExpandedRow] = useState<number | null>(null);

    if (responses.length === 0) {
        return (
            <div className={styles.empty}>
                <p>No hay respuestas aun</p>
            </div>
        );
    }

    const toggleRow = (id: number) => {
        setExpandedRow((prev) => (prev === id ? null : id));
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th className={`${styles.th} ${styles.thTimestamp}`}>Fecha</th>
                            <th className={`${styles.th} ${styles.thRespondent}`}>Respondente</th>
                            {fields.map((f) => (
                                <th key={f.id} className={styles.th}>
                                    <span className={styles.thLabel}>{f.label}</span>
                                </th>
                            ))}
                            <th className={styles.th} />
                        </tr>
                    </thead>
                    <tbody>
                        {responses.map((resp) => {
                            const isExpanded = expandedRow === resp.id;
                            const answerMap = Object.fromEntries(
                                resp.answers.map((a) => [a.field_id, a.value])
                            );
                            return (
                                <Fragment key={resp.id}>
                                    <tr
                                        className={`${styles.tr} ${isExpanded ? styles.trExpanded : ''} ${onRowClick ? styles.trClickable : ''}`}
                                        onClick={() => onRowClick ? onRowClick(resp) : toggleRow(resp.id)}
                                    >
                                        <td className={`${styles.td} ${styles.tdTimestamp}`}>
                                            {formatDistanceToNow(new Date(resp.created), {
                                                addSuffix: true,
                                                locale: es,
                                            })}
                                        </td>
                                        <td className={`${styles.td} ${styles.tdRespondent}`}>
                                            {resp.respondent ? resp.respondent.display_name : 'Anonimo'}
                                        </td>
                                        {fields.map((f) => (
                                            <td key={f.id} className={styles.td}>
                                                <span className={styles.cellValue}>
                                                    {renderValue(answerMap[f.id] ?? '')}
                                                </span>
                                            </td>
                                        ))}
                                        <td className={styles.td}>
                                            <button
                                                className={styles.expandBtn}
                                                type="button"
                                                aria-label={isExpanded ? 'Contraer' : 'Expandir'}
                                                onClick={(e) => { e.stopPropagation(); toggleRow(resp.id); }}
                                            >
                                                {isExpanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
                                            </button>
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <tr className={styles.expandedRow}>
                                            <td colSpan={fields.length + 3}>
                                                <div className={styles.expandedContent}>
                                                    {resp.answers.map((answer) => (
                                                        <div key={answer.id} className={styles.answerDetail}>
                                                            <span className={styles.answerLabel}>{answer.field_label}</span>
                                                            <span className={styles.answerValue}>
                                                                {renderValue(answer.value)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
